import { assertNever } from '@kofno/piper';
import { observer } from 'mobx-react';
import { UserStatus } from '../../../../../../../ParticipantsStore/Types';
import { TPlainTextKey } from '../../../../../../../Translations';

interface Props {
  initials: string;
  status: UserStatus;
}

/**
 * Tailwind opacity modifiers (bg-primary/10) compile to nothing against this
 * app's CSS-variable colors, so the 10% primary tint is mixed inline.
 */
const PRIMARY_TINT = 'color-mix(in srgb, var(--primary) 10%, transparent)';

const statusColor = (status: UserStatus): string => {
  switch (status) {
    case 'active':
      return 'var(--accent)';
    case 'disabled':
      return 'var(--muted-foreground)';
    default:
      return assertNever(status);
  }
};

/** Screen-reader text for the status dot, which is otherwise color alone. */
export const statusLabel = (status: UserStatus): TPlainTextKey => {
  switch (status) {
    case 'active':
      return 'Active account';
    case 'disabled':
      return 'Disabled account';
    default:
      return assertNever(status);
  }
};

function Avatar({ initials, status }: Props) {
  return (
    <div className="relative flex-shrink-0" aria-hidden="true">
      <div
        className="flex h-8 w-8 items-center justify-center rounded-full text-primary"
        style={{
          backgroundColor: PRIMARY_TINT,
          fontSize: 'var(--text-xs)',
          fontWeight: 'var(--font-weight-semibold)',
        }}
      >
        {initials}
      </div>
      <span
        className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-card"
        style={{ backgroundColor: statusColor(status) }}
      />
    </div>
  );
}

export default observer(Avatar);
